import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Image, CheckCircle, Target, Calendar as CalendarIcon,
  BookOpen, Trophy, DollarSign, Menu, X, Palette, LogOut, Crown, Globe, Brain,
  Activity, AlertTriangle, HelpCircle, UserCircle, Sparkles, Lightbulb
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import api from '../lib/api';
import NotificationCenter from './NotificationCenter';
import OnboardingTour from './OnboardingTour';
import TutorialChat from './TutorialChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showThemeDialog, setShowThemeDialog] = useState(false);
  const { theme, currentTheme, setCurrentTheme, themes } = useTheme();
  const { user, logout } = useAuth();
  const { lang, setLang, languages, t } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const [dlocalHealth, setDlocalHealth] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const { data } = await api.get('/dlocal/health');
        if (!cancelled) setDlocalHealth(data);
      } catch (err) {
        if (!cancelled) setDlocalHealth({ ok: false, error: err.response?.data?.detail || 'unreachable' });
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  const navigation = [
    { name: t('dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('visionboard'), href: '/vision-board', icon: Image },
    { name: t('habits'), href: '/habits', icon: CheckCircle },
    { name: t('goals'), href: '/goals', icon: Target },
    { name: t('calendar'), href: '/calendar', icon: CalendarIcon },
    { name: t('journal'), href: '/journal', icon: BookOpen },
    { name: 'Tareas', href: '/breakdown', icon: Sparkles },
    { name: 'Lluvia de ideas', href: '/brainstorm', icon: Lightbulb },
    { name: t('achievements'), href: '/achievements', icon: Trophy },
    { name: t('budget'), href: '/budget', icon: DollarSign },
    { name: t('clarity'), href: '/clarity', icon: Brain },
    { name: 'Mi Perfil', href: '/profile', icon: UserCircle },
    { name: 'Membresía', href: '/membership', icon: Crown },
  ];

  const handleThemeChange = async (themeId) => {
    setCurrentTheme(themeId);
    try {
      await axios.put(`${API}/auth/theme`, { theme: themeId }, { withCredentials: true });
    } catch {
      // theme persists locally via ThemeContext even if API fails
    }
    setShowThemeDialog(false);
  };

  const openHelp = () => window.dispatchEvent(new Event('teko-tour-open'));

  return (
    <div className="min-h-screen relative" style={{ background: theme.bg }}>
      {/* Subtle ambient gradient backdrop — grid removed for a cleaner look */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: `
          radial-gradient(circle at 15% 0%, ${theme.primary}28, transparent 45%),
          radial-gradient(circle at 85% 8%, ${theme.accent}18, transparent 40%),
          radial-gradient(circle at 10% 100%, ${theme.accent}22, transparent 50%),
          radial-gradient(circle at 95% 90%, ${theme.primary}15, transparent 45%)
        `,
      }} />
      <div className="fixed inset-x-0 top-0 h-px pointer-events-none z-0" style={{
        background: `linear-gradient(90deg, transparent, ${theme.primary}40, ${theme.accent}40, ${theme.primary}40, transparent)`,
      }} />

      <div className="relative z-10">
        {/* ===== Desktop Sidebar (>= lg) ===== */}
        <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-64 p-6 z-50 overflow-y-auto"
          style={{ background: theme.bg, borderRight: `1px solid ${theme.border}` }}>
          <div className="mb-8">
            <div className="flex items-center gap-2.5 mb-1">
              <img src="/teko-logo.png" alt="Teko" className="w-10 h-10 object-contain" />
              <h1 className="text-2xl font-light tracking-tight" style={{ color: theme.accent }}>Teko</h1>
            </div>
            <p className="text-xs mt-1" style={{ color: theme.textMuted }}>{user?.name || t('your_personal_space')}</p>
            {user?.is_premium && (
              <span className="inline-flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded"
                style={{ background: `${theme.primary}30`, color: theme.accent }}>
                <Crown size={12} />{t('premium')}
              </span>
            )}
          </div>

          <nav className="flex-1 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link key={item.name} to={item.href} data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200"
                  style={{
                    background: isActive ? `${theme.primary}20` : 'transparent',
                    color: isActive ? theme.accent : theme.textMuted,
                    border: isActive ? `1px solid ${theme.border}` : '1px solid transparent',
                  }}>
                  <Icon size={18} strokeWidth={1.5} />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Notifications + Help + theme + lang + logout */}
          <div className="space-y-2 pt-4" style={{ borderTop: `1px solid ${theme.border}` }}>
            {isAdmin && (
              <div
                data-testid="admin-payments-health"
                title={dlocalHealth ? JSON.stringify(dlocalHealth, null, 2) : 'Verificando...'}
                className="mx-2 mb-1 rounded-lg px-3 py-2 flex items-center gap-2"
                style={{
                  background: dlocalHealth?.ok ? `${theme.success}15` : `${theme.error}15`,
                  border: `1px solid ${dlocalHealth?.ok ? `${theme.success}50` : `${theme.error}50`}`,
                }}>
                {dlocalHealth?.ok
                  ? <Activity size={14} style={{ color: theme.success }} />
                  : <AlertTriangle size={14} style={{ color: theme.error }} />}
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Pasarela · {dlocalHealth?.env || '—'}</p>
                  <p className="text-[11px] font-semibold truncate" style={{ color: dlocalHealth?.ok ? theme.success : theme.error }}>
                    {dlocalHealth === null ? 'Verificando...' : dlocalHealth?.ok ? 'OK · conectado' : 'FAIL · revisar'}
                  </p>
                </div>
              </div>
            )}

            <NotificationCenter />

            <button onClick={openHelp} data-testid="open-tutorial-tour"
              className="flex items-center gap-3 w-full px-4 py-2.5 rounded-md transition-all"
              style={{ color: theme.textMuted }}>
              <HelpCircle size={18} strokeWidth={1.5} />
              <span className="text-sm">Tour de la app</span>
            </button>

            <Dialog open={showThemeDialog} onOpenChange={setShowThemeDialog}>
              <DialogTrigger asChild>
                <button data-testid="theme-selector-button"
                  className="flex items-center gap-3 w-full px-4 py-2.5 rounded-md transition-all"
                  style={{ color: theme.textMuted }}>
                  <Palette size={18} strokeWidth={1.5} />
                  <span className="text-sm">{t('theme')}</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                <DialogHeader>
                  <DialogTitle className="text-xl" style={{ color: theme.accent }}>{t('select_theme')}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {Object.entries(themes).map(([id, t2]) => (
                    <button key={id} data-testid={`theme-${id}`} onClick={() => handleThemeChange(id)}
                      className="p-3 rounded-lg text-left transition-all"
                      style={{
                        background: currentTheme === id ? `${t2.primary}20` : t2.card,
                        border: currentTheme === id ? `2px solid ${t2.primary}` : `1px solid ${t2.border}`,
                      }}>
                      <div className="flex gap-1 mb-2">
                        {t2.preview.map((c, i) => <div key={i} className="w-6 h-6 rounded-full" style={{ background: c }} />)}
                      </div>
                      <span className="text-sm font-medium" style={{ color: t2.textPrimary }}>{t2.name}</span>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            {/* Language selector */}
            <div className="px-2 py-2">
              <p className="text-[10px] uppercase tracking-widest mb-2 px-2" style={{ color: theme.textMuted }}>{t('settings')}</p>
              <div className="flex items-center gap-1.5 px-2 mb-2">
                <Globe size={14} style={{ color: theme.textMuted }} />
                <div className="flex gap-1 flex-1">
                  {languages.map(l => (
                    <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
                      className="flex-1 text-[10px] uppercase tracking-wider py-1 rounded-md transition-colors"
                      style={{
                        background: lang === l.code ? `${theme.primary}30` : 'transparent',
                        color: lang === l.code ? theme.accent : theme.textMuted,
                        border: `1px solid ${lang === l.code ? theme.primary : theme.border}`,
                      }}>
                      {l.code}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button data-testid="logout-button" onClick={logout}
              className="flex items-center gap-3 w-full px-4 py-2.5 rounded-md transition-all"
              style={{ color: theme.textMuted }}>
              <LogOut size={18} strokeWidth={1.5} />
              <span className="text-sm">{t('logout')}</span>
            </button>
          </div>
        </aside>

        {/* ===== Mobile / Tablet Header (< lg) ===== */}
        <header className="lg:hidden sticky top-0 z-40"
          style={{ backdropFilter: 'blur(16px)', background: `${theme.bg}CC`, borderBottom: `1px solid ${theme.border}` }}>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <img src="/teko-logo.png" alt="Teko" className="w-7 h-7 object-contain" />
              <h1 className="text-xl font-light" style={{ color: theme.accent }}>Teko</h1>
            </div>
            <div className="flex items-center gap-1">
              <div className="-mr-1"><NotificationCenter compact /></div>
              <button data-testid="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2" style={{ color: theme.textMuted }}>
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="px-3 pb-24 space-y-1 max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain"
              style={{ borderTop: `1px solid ${theme.border}` }}>
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.name} to={item.href} onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md transition-all"
                    style={{
                      background: isActive ? `${theme.primary}20` : 'transparent',
                      color: isActive ? theme.accent : theme.textMuted,
                    }}>
                    <Icon size={18} strokeWidth={1.5} />
                    <span className="text-sm font-medium">{item.name}</span>
                  </Link>
                );
              })}

              <div className="pt-2 mt-2 space-y-1" style={{ borderTop: `1px solid ${theme.border}` }}>
                <button onClick={() => { setMobileMenuOpen(false); openHelp(); }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm"
                  style={{ color: theme.textMuted }}>
                  <HelpCircle size={18} strokeWidth={1.5} /> Tour de la app
                </button>
                <button onClick={() => { setMobileMenuOpen(false); setShowThemeDialog(true); }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm"
                  style={{ color: theme.textMuted }}>
                  <Palette size={18} strokeWidth={1.5} /> {t('theme')}
                </button>
                <div className="flex items-center gap-2 px-3 py-2">
                  <Globe size={14} style={{ color: theme.textMuted }} />
                  <div className="flex gap-1 flex-1">
                    {languages.map(l => (
                      <button key={l.code} onClick={() => setLang(l.code)}
                        className="flex-1 text-[10px] uppercase tracking-wider py-1 rounded-md"
                        style={{
                          background: lang === l.code ? `${theme.primary}30` : 'transparent',
                          color: lang === l.code ? theme.accent : theme.textMuted,
                          border: `1px solid ${lang === l.code ? theme.primary : theme.border}`,
                        }}>
                        {l.code}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={logout}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm"
                  style={{ color: theme.textMuted }}>
                  <LogOut size={18} strokeWidth={1.5} /> {t('logout')}
                </button>
              </div>
            </div>
          )}
        </header>

        {/* ===== Main content ===== */}
        <main className="lg:ml-64 px-3 sm:px-6 md:px-10 py-4 sm:py-6 md:py-10 pb-24 lg:pb-12">
          <div className="max-w-[1600px] mx-auto">{children}</div>
        </main>

        {/* ===== Mobile / Tablet swipeable bottom-nav (< lg) ===== */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-40"
          style={{
            background: `${theme.bg}EE`,
            backdropFilter: 'blur(20px)',
            borderTop: `1px solid ${theme.border}`,
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          data-testid="mobile-bottom-nav"
        >
          <div
            className="overflow-x-auto snap-x snap-mandatory"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
            data-testid="mobile-bottom-nav-scroll"
          >
            <div className="flex items-stretch" style={{ width: 'max-content', minWidth: '100%' }}>
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`bottom-nav-${item.href.replace('/', '') || 'dashboard'}`}
                    className="flex flex-col items-center justify-center gap-0.5 py-2.5 px-3 snap-start shrink-0 relative"
                    style={{
                      color: isActive ? theme.accent : theme.textMuted,
                      minWidth: '76px',
                      flex: '0 0 auto',
                    }}
                  >
                    <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                    <span
                      className="text-[10px] font-medium whitespace-nowrap"
                      style={{ opacity: isActive ? 1 : 0.75 }}
                    >
                      {item.name}
                    </span>
                    {isActive && (
                      <span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-t-full"
                        style={{ background: theme.accent }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      </div>

      {/* Tutorial overlay + floating chat (mounted once per session) */}
      <OnboardingTour />
      <TutorialChat />
    </div>
  );
};

export default Layout;
